import {
  ButtonRadioGroup,
  Button,
  H4,
  Modal,
  Table,
  Text,
  Tooltip,
} from '@payaca/ambrosia';
import {
  calculateSolarUsNetMeteringLedger,
  type SolarUsNetMeteringLedgerRow,
} from '@payaca/helpers/solarUsNetMeteringLedger';
import { DEFAULT_PANEL_DEGRADATION_PERCENT } from '@payaca/helpers/solarSavingsConstants';
import { solarSiteTimeZone } from '@payaca/helpers/solarSiteTimeZone';
import { FC, useId } from 'react';
import {
  calculateSolarUsNetMeteringPolicy,
  solarUsNetMeteringWeatherProvenance,
  solarUsNetMeteringPolicySchema,
  type SolarUsNetMeteringPolicy,
} from '@payaca/helpers/solarUsNetMeteringPolicy';
import usePreferredNumberFormat from '@/hooks/usePreferredNumberFormat';
import { useInspectorDialog } from './useInspectorDialog';

/** April starts the credit year unless a saved policy says otherwise. */
const DEFAULT_SETTLEMENT_MONTH = 4;

export type UsNetMeteringPolicyInputs = {
  /** The site pin, for its time zone. */
  site?: { lat: number; lng: number } | null;
  annualDemandKwh: number | null;
  /** The savings model's self-use share. */
  selfUseFraction: number;
  /** The account's flat export rate, also the settlement rate. */
  exportCentsPerKwh: number;
  /** Annual output loss from the panel spec, else the 0.5% default. */
  degradationPercent?: number | null;
  today?: Date;
};

/**
 * The monthly credit policy as it stands now. Everything computable is
 * derived at read time (site time zone from the pin, this month for twelve
 * months, demand, self-use, degradation, settlement rate); a saved policy
 * only contributes the installer's choice of a monthly kWh bank and its
 * credit-year month. Null without annual demand.
 */
export const derivedUsNetMeteringPolicy = (
  saved: SolarUsNetMeteringPolicy | null,
  inputs: UsNetMeteringPolicyInputs
): SolarUsNetMeteringPolicy | null => {
  const timeZone = solarSiteTimeZone(inputs.site);
  const today = inputs.today ?? new Date();
  const demand =
    inputs.annualDemandKwh == null
      ? (saved?.demand ?? null)
      : {
          monthlyKwh: Array<number>(12).fill(inputs.annualDemandKwh / 12),
          provenance:
            'Estimate: annual household demand divided equally over twelve months',
        };
  if (!demand) return null;
  const parsed = solarUsNetMeteringPolicySchema.safeParse({
    schemaVersion: 1,
    mode: 'monthlyKwhBank',
    demand,
    siteTimeZone: timeZone.zone,
    timeZoneProvenance: timeZone.provenance,
    startYear: today.getFullYear(),
    startMonth: today.getMonth() + 1,
    months: 12,
    assumedSelfUseFraction: inputs.selfUseFraction,
    wholesaleCentsPerKwh: inputs.exportCentsPerKwh,
    settlementMonth: saved?.settlementMonth ?? DEFAULT_SETTLEMENT_MONTH,
    annualDegradationPercent:
      inputs.degradationPercent ?? DEFAULT_PANEL_DEGRADATION_PERCENT,
    annualRetailGrowthPercent: saved?.annualRetailGrowthPercent ?? 0,
    annualExportGrowthPercent: saved?.annualExportGrowthPercent ?? 0,
  });
  return parsed.success ? parsed.data : null;
};

type LedgerNumericField = {
  [K in keyof SolarUsNetMeteringLedgerRow]: SolarUsNetMeteringLedgerRow[K] extends number
    ? K
    : never;
}[keyof SolarUsNetMeteringLedgerRow];

/**
 * Ledger columns in display order. `always` columns stay visible even when
 * every month is zero; the rest only appear once they carry a value, or when
 * the installer asks for every column. `total: false` marks running balances
 * that have no meaningful sum.
 */
const LEDGER_COLUMNS: ReadonlyArray<{
  field: LedgerNumericField;
  header: string;
  help?: string;
  unit: 'kWh' | 'usd';
  always?: true;
  total?: false;
}> = [
  {
    field: 'generationKwh',
    header: 'Generation',
    unit: 'kWh',
    always: true,
  },
  { field: 'demandKwh', header: 'Demand', unit: 'kWh', always: true },
  {
    field: 'selfUseKwh',
    header: 'Self-use',
    help: 'Assumed share of generation used in the home, capped at demand',
    unit: 'kWh',
    always: true,
  },
  { field: 'importsKwh', header: 'Imports', unit: 'kWh' },
  { field: 'exportsKwh', header: 'Exports', unit: 'kWh', always: true },
  {
    field: 'creditsUsedKwh',
    header: 'Credits used',
    help: 'Banked kWh credit applied against this month’s imports',
    unit: 'kWh',
  },
  {
    field: 'bankClosingKwh',
    header: 'Banked',
    help: 'kWh credit carried into next month',
    unit: 'kWh',
    total: false,
  },
  {
    field: 'settledKwh',
    header: 'Settled',
    help: 'Banked credit cashed out at the annual settlement',
    unit: 'kWh',
  },
  {
    field: 'settlementUsd',
    header: 'Settlement',
    help: 'Settled credit paid at the surplus rate',
    unit: 'usd',
  },
  { field: 'exportPaymentUsd', header: 'Export credit', unit: 'usd' },
  {
    field: 'baselineCostUsd',
    header: 'No solar',
    help: 'Energy cost without solar',
    unit: 'usd',
  },
  {
    field: 'modelEnergyCostUsd',
    header: 'With solar',
    help: 'Energy cost with solar',
    unit: 'usd',
  },
  {
    field: 'benefitUsd',
    header: 'Benefit',
    help: 'Energy cost without solar minus energy cost with solar',
    unit: 'usd',
    always: true,
  },
];

/** "2026-04" → "Apr 2026"; anything unexpected is shown as stored. */
const formatLedgerPeriod = (period: string) => {
  const match = /^(\d{4})-(\d{2})$/.exec(period);
  if (!match) return period;
  return new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, 1)
  ).toLocaleString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

/** Formats with a true minus sign (−$709.67) and never shows a negative zero. */
const formatSigned = (format: Intl.NumberFormat, value: number) => {
  const magnitude = format.format(Math.abs(value));
  return value < 0 && magnitude !== format.format(0)
    ? `\u2212${magnitude}`
    : magnitude;
};

/**
 * Local scenario exploration only; never silently changes an estimate's
 * money. Every input is derived (site time zone, this month for twelve
 * months, panel degradation, the savings model's self-use, account rates);
 * the only choice is how exports are paid.
 */
const UsNetMeteringScenario: FC<{
  policy: SolarUsNetMeteringPolicy | null;
  onPolicyChange: (policy: SolarUsNetMeteringPolicy | null) => void;
  monthlyAcKwh: number[] | null;
  weatherSource?: boolean;
  annualDemandKwh: number | null;
  retailCentsPerKwh: number;
  exportCentsPerKwh: number;
  selfUseFraction: number;
  /** The site, for its time zone. */
  site?: { lat: number; lng: number } | null;
  /** Annual output loss from the panel spec, else the 0.5% default. */
  degradationPercent?: number | null;
}> = (props) => {
  const {
    policy,
    onPolicyChange,
    monthlyAcKwh,
    weatherSource,
    annualDemandKwh,
    retailCentsPerKwh,
    exportCentsPerKwh,
    selfUseFraction,
    site,
    degradationPercent,
  } = props;
  const [isOpen, toggleEditor] = useInspectorDialog('#solar-credit-policy');
  const scenario = derivedUsNetMeteringPolicy(policy, {
    site,
    annualDemandKwh,
    selfUseFraction,
    exportCentsPerKwh,
    degradationPercent,
  });
  const mode = policy ? 'monthlyKwhBank' : 'flatExport';
  const number = usePreferredNumberFormat({ maximumFractionDigits: 2 });
  const wholeNumber = usePreferredNumberFormat({ maximumFractionDigits: 0 });
  const ledgerHeadingId = useId();
  const result = (() => {
    if (!scenario) return null;
    if (policy)
      return calculateSolarUsNetMeteringPolicy(
        scenario,
        monthlyAcKwh,
        retailCentsPerKwh,
        undefined,
        weatherSource ? solarUsNetMeteringWeatherProvenance : undefined
      );
    return calculateSolarUsNetMeteringLedger({
      generation:
        monthlyAcKwh === null
          ? null
          : {
              monthlyAcKwh,
              provenance: weatherSource
                ? solarUsNetMeteringWeatherProvenance
                : 'Selected panels: annual AC source output weighted by stored seasonal shares',
            },
      demand: scenario.demand,
      siteTimeZone: scenario.siteTimeZone,
      timeZoneProvenance: scenario.timeZoneProvenance,
      startYear: scenario.startYear,
      startMonth: scenario.startMonth,
      months: scenario.months,
      assumedSelfUseFraction: scenario.assumedSelfUseFraction,
      retailCentsPerKwh,
      compensation: { mode: 'flatExport', exportCentsPerKwh },
      annualDegradationPercent: scenario.annualDegradationPercent,
      annualRetailGrowthPercent: scenario.annualRetailGrowthPercent,
      annualExportGrowthPercent: scenario.annualExportGrowthPercent,
      costBasis: {
        kind: 'incomplete',
        description:
          'Designer equipment pricing is not a confirmed installed system cost',
      },
    });
  })();
  const ledgerRows = result?.status === 'available' ? result.snapshot.rows : [];
  const formatLedgerValue = (value: number) => formatSigned(wholeNumber, value);
  // A column shows when it always does or any month is non-zero.
  const visibleLedgerColumns = LEDGER_COLUMNS.filter(
    ({ field, always }) =>
      always ||
      ledgerRows.some(
        (row) => formatLedgerValue(row[field]) !== formatLedgerValue(0)
      )
  );
  const monthName = (month: number) =>
    new Date(2024, month - 1).toLocaleString('en-US', { month: 'long' });
  return (
    <section
      aria-label="Energy credit policy"
      className="space-y-3 border-t border-gray-200 pt-4"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="space-y-1">
          <H4>Energy credits</H4>
          <Text level="supporting">
            {policy
              ? `Monthly rollover · ${monthName(policy.settlementMonth)} credit year`
              : 'Flat export'}
          </Text>
        </div>
        <Button variant="secondary" size="sm" onClick={toggleEditor}>
          Edit tariff
        </Button>
      </div>
      <Modal
        title="Energy credit policy and monthly ledger"
        size="xl"
        isOpen={isOpen}
        onClose={toggleEditor}
        disableBackdropClick={false}
      >
        <Modal.Body className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Text as="span">
              <strong>Export</strong>
            </Text>
            <ButtonRadioGroup
              aria-label="Export"
              value={mode}
              onChange={(next) => {
                if (next === 'flatExport') onPolicyChange(null);
                else if (!policy && scenario) onPolicyChange(scenario);
              }}
              options={[
                {
                  label: `Flat ${number.format(exportCentsPerKwh)}¢`,
                  value: 'flatExport',
                },
                {
                  label: 'Monthly kWh bank',
                  value: 'monthlyKwhBank',
                  disabled: !policy && !scenario,
                },
              ]}
            />
          </div>
          {scenario && (
            <Text level="supporting">
              {[
                `Retail ${number.format(retailCentsPerKwh)}¢/kWh`,
                `${formatLedgerPeriod(`${scenario.startYear}-${String(scenario.startMonth).padStart(2, '0')}`)} · ${scenario.months} months`,
                scenario.siteTimeZone,
                `Self-use ${number.format(scenario.assumedSelfUseFraction * 100)}%`,
                `Degradation ${number.format(scenario.annualDegradationPercent)}%/yr`,
              ].join(' · ')}
            </Text>
          )}
          {result?.status === 'available' ? (
            <div
              role="group"
              aria-labelledby={ledgerHeadingId}
              className="space-y-2"
            >
              <H4 id={ledgerHeadingId}>Scenario energy ledger</H4>
              <Table<SolarUsNetMeteringLedgerRow>
                items={ledgerRows}
                uniqueKey="period"
                footerColumns={[
                  {
                    span: 1,
                    className: 'normal-case',
                    render: () => 'Total',
                  },
                  ...visibleLedgerColumns.map(({ field, total }) => ({
                    span: 1,
                    className: 'normal-case whitespace-nowrap text-end',
                    render: () =>
                      total === false
                        ? ''
                        : formatLedgerValue(
                            ledgerRows.reduce((sum, row) => sum + row[field], 0)
                          ),
                  })),
                ]}
              >
                <Table.Column<SolarUsNetMeteringLedgerRow, 'period'>
                  header="Period"
                  field="period"
                  className="normal-case whitespace-nowrap py-2"
                  render={(period) => formatLedgerPeriod(period)}
                />
                {visibleLedgerColumns.map(({ field, header, help, unit }) => (
                  <Table.Column<SolarUsNetMeteringLedgerRow, LedgerNumericField>
                    key={field}
                    header={
                      <span className="block whitespace-normal contain-inline-size">
                        <span className="block">
                          {help ? (
                            <Tooltip tooltipContent={help} placement="top">
                              <span
                                tabIndex={0}
                                className="cursor-help underline decoration-dotted underline-offset-2"
                              >
                                {header}
                              </span>
                            </Tooltip>
                          ) : (
                            header
                          )}
                        </span>
                        <span className="block font-normal text-gray-500">
                          {unit === 'kWh' ? 'kWh' : '$'}
                        </span>
                      </span>
                    }
                    field={field}
                    align="end"
                    className="normal-case whitespace-nowrap tabular-nums py-2"
                    render={(value) => formatLedgerValue(value)}
                  />
                ))}
              </Table>
            </div>
          ) : (
            <Text level="supporting">
              {monthlyAcKwh === null
                ? 'Calculate PVWatts generation to see monthly savings: choose Calculate in Performance. You can continue pricing your quote.'
                : (result?.message ?? 'Add annual use to see the ledger.')}
            </Text>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button variant="secondary" onClick={toggleEditor}>
              Done
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default UsNetMeteringScenario;
